import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import { useTranslation } from "react-i18next";
import { useHistory, useParams } from "react-router-dom";
// shared
import { DEFAULT_MAX_PER_PAGE } from "Components/shared/constants";
import ModuleTitleBar from "Components/shared/module-title-bar";
import { UsersList } from "Components/user-management/user-list";
import { UserProfileTwo } from "Components/user-management/user-profile";
// helpers
import { decode, encode, toaster, toNum } from "Helpers/index";
// hooks
import { useAccessControlList, useItemsPerPage, useQuery, useSession } from "Hooks/index";
import usePagination from "Hooks/pagination.hook";

// services
import { fetchSingleUser, fetchUserList, userActivation } from "Services/user.services";
// types
import { AccountRole, Sorting, SortingMode, Statuses } from "Types/common.types";
import { fetchDiolkoCompaniesList } from "Services/new-driver-management.services";
import { fetchCustomerList } from "Services/customer.service";
import { FetchedUserData } from "Types/user.types";
import {useRecoilState} from "recoil";
import {GlobalFilterAtom} from "Recoil/global_filter.atom";
import {isEmpty, isNil, omitBy} from "lodash";

const InactiveUsersListing = () => {
  const { t } = useTranslation();
  const { org, profile, roleTypes } = useSession();
  const permission = useAccessControlList({
    roleTypes: roleTypes,
    resource: "User_Management",
  });

  // permissions
  const canList = permission.rights.includes("LIST");
  const canView = permission.rights.includes("VIEW");
  const canEdit = permission.rights.includes("EDIT");
  const canDelete = permission.rights.includes("DELETE");

  const { id, orgID } = useParams<{ id: string; orgID: string }>();
  const query = useQuery();
  const history = useHistory();

  const tabKey = query.get("tabKey") || "0";
  const page = query.get("page") || 1;
  const userID = decode(id) || "";
  const decodedOrgID = decode(orgID) || "";

  const [usersList, setUsersList] = useState<FetchedUserData[]>([]);
  const [userDetails, setUserDetails] = useState<FetchedUserData>({
    userRole: {
      status: "DEACTIVATED",
    },
  });
  const [globalFilterState, ] = useRecoilState(GlobalFilterAtom);
  let initialFilters = {
    status: "DEACTIVATED",
    ancestorOrgIDs : globalFilterState?.selectedOrgIDs?.length ? globalFilterState?.selectedOrgIDs : null
  };
  const initialSorting = { sortBy: "fullName", mode: SortingMode.DESC };
  const initialQueryParams = { page, offset: (toNum(page) - 1) * toNum(itemPerPage) };

  const [filters, setFilters] = useState<{ [key: string]: any }>(
    initialFilters
  );
  const [sorting, setSorting] = useState<Sorting>(initialSorting);
  const [companiesList, setCompaniesList] = useState([]);
  const [customersList, setCustomersList] = useState([]);
  const [selectedOrg, setSelectedOrg] = useState();

  const [currentListCount, setCurrentListCount] = useState(0);
  const { paginationComponent, itemPerPage, queryParams, setQueryParams } = usePagination({
    currentListCount,
  });

  useEffect(() => {
    if (org.orgType === AccountRole.LOGISTIC_PROVIDER) {
      handleFetchCompanies();
    }
    if (org.orgType !== AccountRole.CUSTOMER) {
      handleFetchCustomers();
    }
  }, []);

  useEffect(() => {
    if (userID) {
      handleFetchUserDetails();
    }
  }, [userID]);

  useEffect(()=>{
    if(!isEmpty(globalFilterState) && globalFilterState?.selectedOrgIDs?.length){
      setFilters({...filters, ancestorOrgIDs : globalFilterState?.selectedOrgIDs})
    }
  },[globalFilterState])

  useEffect(() => {
    if (!userID) {
      handleFetchUserList();
    }
  }, [queryParams.offset, filters, sorting, itemPerPage, selectedOrg]);

  const tabKeyPaginationProps = {
    tabKey: tabKey,
    pathWithPage: {
      withTabKey: `/user-management/users?tabKey=${tabKey}&page=${page}`,
      withoutTabKey: `/user-management/users?page=${page}`,
    },
    pathWithoutPage: {
      withTabKey: `/user-management/users?tabKey=${tabKey}`,
      withoutTabKey: "/user-management/users",
    },
  };

  const handleFetchCompanies = async () => {
    const responseData = await fetchDiolkoCompaniesList(org._id);

    if (responseData && !responseData.error) {
      // setCompaniesList(responseData);
    }
  };

  const handleFetchCustomers = async () => {
    const responseData = await fetchCustomerList({}, org._id, Statuses.APPROVED);

    if (responseData && !responseData.error) {
      const customersOption = responseData.map((data) => {
        return {
          label: data.name,
          value: data._id,
        };
      });
      setCustomersList(customersOption);
    }
  };

  const handleFetchUserList = async () => {
    const responseData = await fetchUserList(
      org.orgType,
      org._id,
      selectedOrg ? [selectedOrg] : [org._id],
      org.orgType,
      omitBy(filters,isNil),
      {
        offset: queryParams.offset,
        limit: toNum(itemPerPage),
      },
      sorting
    );

    if (responseData && !responseData.error) {
      setUsersList(responseData);
      setCurrentListCount(responseData.length);
    } else {
      toaster("error", responseData.error.message);
    }
  };

  const handleFetchUserDetails = async () => {
    const responseData = await fetchSingleUser(profile._id, org._id, userID, decodedOrgID);

    if (responseData && !responseData.error) {
      setUserDetails(responseData);
    } else {
      toaster("error", responseData?.error?.message);
    }
  };

  const handleActivateUser = async () => {
    const responseData = await userActivation(profile._id, userID, "ACTIVE");

    if (responseData && !responseData.error) {
      toaster("success", t("toaster.activateUserSuccessfully"));
      history.push("/user-management/users/inactive");
      handleFetchUserList();
    } else {
      toaster("error", t("toaster.activateUserFailed"));
      history.push("/user-management/users/inactive");
      handleFetchUserList();
    }
  };

  const handleViewUserProfile = (orgID, profileID) => {
    const id = encode(profileID);
    const encodedOrgID = encode(orgID);
    history.push(`/user-management/user/deactivated/${encodedOrgID}/${id}/profile`);
  };

  const handleSelectOrg = (orgID) => {
    setSelectedOrg(orgID);
  };

  const handleFilter = (filterInput) => {
    const filter = {
      ...filters,
      orFields: filterInput.orFields,
      roleSubType: filterInput.roles,
    };
    setFilters(filter);
  };

  // function that returns user profile component
  const handleReturnUserView = () => {
    return (
      <UserProfileTwo
        canEdit={canEdit}
        canDelete={canDelete}
        userDetails={userDetails}
        onBack={() => {
          history.goBack();
        }}
        onActivateUser={() => handleActivateUser()}
      />
    );
  };

  return (
    <div>
      <Helmet>
        <title>{t("pageTitle.inactiveUser")}</title>
      </Helmet>

      <ModuleTitleBar
        titleText={t("title.userManagement")}
        haveSubtitle
        subtitleText={t("title.inactiveUser")}
      />

      {id ? (
        handleReturnUserView()
      ) : (
        <UsersList
          canList={canList}
          canView={canView}
          canEdit={canEdit}
          canDelete={canDelete}
          usersList={usersList}
          companiesList={companiesList}
          customersList={customersList}
          onSelectOrg={(orgID) => handleSelectOrg(orgID)}
          onFilter={(filterInput) => handleFilter(filterInput)}
          onViewProfile={(orgID, profileID) =>
            handleViewUserProfile(orgID, profileID)
          }
          onActivateUser={() => handleActivateUser()}
        />
      )}

      <div className="my-30">{paginationComponent}</div>
    </div>
  );
};

export default InactiveUsersListing;
